import { useState, useEffect, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from "recharts";
import { cCourseIstDevelopment, cSubmissionIst } from "@/lib/api";
import { ThinkingStructureMap as ThinkingMap } from "@/components/ThinkingStructureMap";
import { ThinkingLevelReference } from "@/components/ThinkingLevelReference";

const IST = "#2F6F6A"; // IST accent (deep teal) — visually distinct from WSR clay #8C3A2A.

// Coordinate-over-time trend for Thinking Structure (1.0-7.0). Secondary to the maps; raw observations,
// never smoothed. The gate line at 4.0 marks representational (below) vs abstract (above).
function IstTrendChart({ data, lines, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-md p-4" data-testid={testid}>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 10, right: 24, bottom: 8, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
          <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#78716c" }} />
          <YAxis domain={[1, 7]} ticks={[1, 2, 3, 4, 5, 6, 7]} tick={{ fontSize: 12, fill: "#78716c" }} />
          <ReferenceLine y={4} stroke="#2F6F6A" strokeDasharray="4 4"
            label={{ value: "abstract ↑ · representational ↓", position: "insideTopRight", fontSize: 10, fill: "#2F6F6A" }} />
          <Tooltip contentStyle={{ fontSize: 13, borderRadius: 6, border: "1px solid #e7e5e4" }} formatter={(v) => (v == null ? "—" : v)} />
          <Legend wrapperStyle={{ fontSize: 13 }} />
          {lines.map((l) => (
            <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} strokeWidth={2.5}
              strokeDasharray={l.dashed ? "6 4" : undefined} dot={{ r: 4, fill: l.color }} activeDot={{ r: 6 }} connectNulls={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <p className="mt-2 text-[11px] text-stone-400">Coordinate ≈1–3.5 = organizing concrete ideas · ≈4–7 = constructing and organizing generalizations. Up or down is a different performance, not automatically improvement or decline.</p>
    </div>
  );
}

const confCls = (c) => ({
  HIGH: "bg-emerald-50 text-emerald-700 border-emerald-200",
  MODERATE: "bg-amber-50 text-amber-700 border-amber-200",
  LOW: "bg-rose-50 text-rose-700 border-rose-200",
}[(c || "").toUpperCase()] || "bg-stone-50 text-stone-500 border-stone-200");

const dir = (a, b) => (a == null || b == null ? "" : b > a ? "↑" : b < a ? "↓" : "→");

export function IstOrientation({ compact }) {
  return (
    <span className={`text-[13px] text-stone-500 ${compact ? "" : "block bg-[#2F6F6A]/5 border border-[#2F6F6A]/20 rounded-md p-3"}`} data-testid="ist-orientation">
      This describes the organization of thinking expressed in a particular piece of writing — not a fixed level of the
      student. It is expected to move up or down across assignments and revisions.
    </span>
  );
}

export function IstBadge({ c, testid }) {
  if (!c) return <span className="text-[12px] text-stone-300">not analyzed</span>;
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid={testid}>
      <span className="text-[12px] font-medium text-stone-700">{c.form}</span>
      <span className="text-[11px] text-stone-400">≈{c.ist}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${confCls(c.confidence)}`}>{c.confidence}</span>
      {c.watch && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700">provisional</span>}
    </span>
  );
}

function ExpandButton({ analysis, role }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="expand-map" className="text-[12px] text-[#2F6F6A] hover:underline">Expand map ⤢</button>
      {open && (
        <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-6" onClick={() => setOpen(false)} data-testid="map-modal">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl text-stone-900">{role} — Thinking Structure Map</h3>
              <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700 text-xl" data-testid="map-modal-close">✕</button>
            </div>
            <ThinkingMap analysis={analysis} big />
            {analysis?.map_notes && <p className="mt-4 text-[14px] text-stone-700 bg-[#2F6F6A]/5 border border-[#2F6F6A]/15 rounded-md p-3">{analysis.map_notes}</p>}
            {analysis?.teacher_map && (
              <details className="mt-4">
                <summary className="text-[12px] text-stone-400 cursor-pointer">Raw analyzer map</summary>
                <pre className="mt-2 whitespace-pre overflow-x-auto bg-stone-50 border border-stone-200 rounded-sm p-3 text-[12px] text-stone-700">{analysis.teacher_map}</pre>
              </details>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function TechnicalDetails({ a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border-t border-stone-100 pt-2">
      <button onClick={() => setOpen(!open)} className="text-[12px] text-stone-500 hover:text-stone-800" data-testid="ist-technical-toggle">
        Technical details {open ? "–" : "+"}
      </button>
      {open && (
        <div className="mt-2 flex flex-col gap-2 text-[12.5px]" data-testid="ist-technical-body">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-stone-700">{a.skill_theory_form} · ≈{a.ist_numeric}</span>
            <span className={`px-2 py-0.5 rounded-full border text-[11px] ${confCls(a.confidence)}`}>confidence: {a.confidence}</span>
            {a.at_watch_boundary && <span className="px-2 py-0.5 rounded-full border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 text-[11px]">watch boundary</span>}
            <span className="text-stone-400">{a.skill_theory_name}</span>
          </div>
          {a.structural_rationale && <p className="text-stone-600"><span className="text-stone-400">Why: </span>{a.structural_rationale}</p>}
          {a.boundary_to_next && <p className="text-stone-500"><span className="text-stone-400">Next: </span>{a.boundary_to_next}</p>}
          {a.performance_in_context && <p className="text-[11px] italic text-stone-400">{a.performance_in_context}</p>}
        </div>
      )}
    </div>
  );
}

function ThinkingStep({ row, big }) {
  const a = row?.analysis || {};
  const provisional = a.confidence === "LOW" || a.at_watch_boundary;
  return (
    <div className={`bg-white border border-stone-200 rounded-md ${big ? "p-6" : "p-5"}`} data-testid={`ist-step-${row.iteration_number}`}>
      <div className="flex items-center justify-between mb-4">
        <span className={`font-mono uppercase tracking-wider text-[#2F6F6A] ${big ? "text-[13px] font-semibold" : "text-[12px]"}`}>{roleLabel(row.iteration_role)}</span>
        {!a.error && <ExpandButton analysis={a} role={roleLabel(row.iteration_role)} />}
      </div>
      {a.error ? <p className="text-[13px] text-stone-400">Analysis unavailable for this version.</p> : (
        <>
          <ThinkingMap analysis={a} big={big} />
          {a.map_notes && <p className={`mt-5 leading-relaxed text-stone-800 ${big ? "text-[15px]" : "text-[14px]"}`} data-testid={`ist-plain-${row.iteration_number}`}>{a.map_notes}</p>}
          {provisional && <p className="mt-3 text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-2.5 py-1.5" data-testid={`ist-provisional-${row.iteration_number}`}>Read the map — this estimate is uncertain.</p>}
          <TechnicalDetails a={a} />
        </>
      )}
    </div>
  );
}

const roleLabel = (r) => ({ entry: "Initial", final: "Developed" }[r] || (r ? r[0].toUpperCase() + r.slice(1) : "Version"));

// WITHIN A PIECE — Initial | Developed side by side (maps primary); intermediates behind disclosure.
export function IstWithin({ sessionId }) {
  const [data, setData] = useState(null);
  const [showMid, setShowMid] = useState(false);
  const load = useCallback(async () => setData(await cSubmissionIst(sessionId)), [sessionId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-[13px] text-stone-400" data-testid="ist-within-loading">Loading thinking maps…</p>;
  const rows = data.analyses || [];
  const initial = rows[0], developed = rows.length > 1 ? rows[rows.length - 1] : null;
  const mids = rows.slice(1, Math.max(1, rows.length - 1));
  const withinTrend = rows.map((r) => ({ name: roleLabel(r.iteration_role), value: r.analysis?.ist_numeric ?? null }));
  return (
    <div data-testid="ist-within">
      <h3 className="font-serif text-2xl text-stone-900">How the organization of thinking changed in this piece</h3>
      <p className="mt-1 mb-4"><IstOrientation compact /></p>
      {rows.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-300 rounded-md p-8 text-center" data-testid="ist-within-missing">
          <p className="text-sm text-stone-500">Thinking has not been analyzed for this piece yet.</p>
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-6" data-testid="ist-within-pair">
            <ThinkingStep row={initial} big />
            {developed ? <ThinkingStep row={developed} big /> : <div className="bg-stone-50 border border-dashed border-stone-300 rounded-md p-6 text-center text-[13px] text-stone-400">Only one version submitted.</div>}
          </div>
          {withinTrend.filter((t) => t.value != null).length > 1 && (
            <div className="mt-6" data-testid="ist-within-trend-wrap">
              <h4 className="text-[12px] font-mono uppercase tracking-wider text-stone-400 mb-2">Thinking Structure across revisions</h4>
              <IstTrendChart testid="ist-within-trend" data={withinTrend} lines={[{ key: "value", name: "Thinking Structure coordinate", color: IST }]} />
            </div>
          )}
          {mids.length > 0 && (
            <div className="mt-3">
              <button onClick={() => setShowMid(!showMid)} data-testid="ist-view-intermediate" className="text-[13px] text-[#2F6F6A] hover:underline">
                {showMid ? "Hide intermediate revisions" : `View intermediate revisions (${mids.length})`}
              </button>
              {showMid && <div className="mt-3 grid md:grid-cols-2 gap-4" data-testid="ist-within-mids">{mids.map((m) => <ThinkingStep key={m.iteration_number} row={m} />)}</div>}
            </div>
          )}
        </>
      )}
      <ThinkingLevelReference />
    </div>
  );
}

// Plain-language summary card for the Development Overview ("Organization of Thinking"). Words lead; number hidden.
export function IstThinkingSummary({ courseId, studentId, onExplore }) {
  const [data, setData] = useState(null);
  useEffect(() => { cCourseIstDevelopment(courseId).then(setData); }, [courseId]);
  let body = <p className="text-[13px] text-stone-400" data-testid="ist-summary-loading">Loading…</p>;
  if (data) {
    const student = data.series.find((s) => s.student_id === studentId);
    const analyzed = (student?.points || []).filter((p) => p.analyzed);
    if (!analyzed.length) {
      body = <p className="text-[13px] text-stone-500" data-testid="ist-summary-none">No thinking analyses stored for this student yet.</p>;
    } else {
      const first = analyzed[0], last = analyzed[analyzed.length - 1];
      body = (
        <div className="space-y-2 text-[14px] leading-relaxed text-stone-700" data-testid="ist-summary-body">
          <p><span className="text-stone-400 text-[12px] uppercase tracking-wider">Earlier · {first.assignment_title}</span><br />{first.entry?.notes || first.final?.notes}</p>
          <p><span className="text-stone-400 text-[12px] uppercase tracking-wider">More developed · {last.assignment_title}</span><br />{last.final?.notes || last.entry?.notes}</p>
        </div>
      );
    }
  }
  return (
    <div className="bg-white border border-[#2F6F6A]/30 rounded-lg p-5" data-testid="overview-thinking">
      <h3 className="text-[15px] font-semibold text-[#22504c] mb-1">Organization of Thinking</h3>
      <p className="text-[12px] text-stone-400 mb-3">What relationships among ideas the student expresses.</p>
      {body}
      <button onClick={onExplore} data-testid="explore-thinking" className="mt-4 text-[13px] font-medium text-[#2F6F6A] hover:underline">Explore thinking development →</button>
    </div>
  );
}

// Compare up to 4 assignments' Thinking Structure Maps. Reuses GET /ist/{session} per selected session (reads only).
export function IstCompare({ picks, onClose }) {
  const [byId, setById] = useState({});
  const [mode, setMode] = useState("developed");
  useEffect(() => {
    picks.forEach((p) => cSubmissionIst(p.session_id).then((d) => setById((m) => ({ ...m, [p.session_id]: d.analyses || [] }))));
  }, [picks]);
  const pick = (rows, which) => which === "initial" ? rows?.[0] : (rows?.length > 1 ? rows[rows.length - 1] : rows?.[0]);
  const tabs = [["developed", "Developed"], ["initial", "Initial"], ["pair", "Initial → Developed"]];
  return (
    <div className="mt-4" data-testid="ist-compare">
      <p className="text-[12px] font-mono uppercase tracking-wider text-[#2F6F6A] mb-2" data-testid="compare-context">Comparing {picks.length} assignment{picks.length === 1 ? "" : "s"} · maps &amp; explanations</p>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="inline-flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-md p-1" data-testid="compare-mode-tabs">
          {tabs.map(([m, l]) => (
            <button key={m} data-testid={`compare-mode-${m}`} onClick={() => setMode(m)}
              className={`px-3 py-1.5 text-sm rounded-sm ${mode === m ? "text-white" : "text-stone-600 hover:text-stone-900"}`}
              style={mode === m ? { background: "#2F6F6A" } : {}}>{l}</button>
          ))}
        </div>
        <button onClick={onClose} className="text-[13px] text-stone-500 hover:text-stone-800" data-testid="compare-close">← Back to assignments</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {picks.map((p) => {
          const rows = byId[p.session_id];
          if (mode === "pair") {
            return (
              <div key={p.session_id} className="border border-stone-200 rounded-md p-3 bg-white md:col-span-2" data-testid={`compare-pair-${p.assignment_id}`}>
                <p className="text-[13px] font-medium text-stone-800 mb-2">{p.assignment_title}</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div><p className="text-[11px] uppercase tracking-wider text-stone-400 mb-1">Initial</p><ThinkingMap analysis={pick(rows, "initial")?.analysis} />{pick(rows, "initial")?.analysis?.map_notes && <p className="mt-2 text-[13px] text-stone-700">{pick(rows, "initial").analysis.map_notes}</p>}</div>
                  <div><p className="text-[11px] uppercase tracking-wider text-stone-400 mb-1">Developed</p><ThinkingMap analysis={pick(rows, "developed")?.analysis} />{pick(rows, "developed")?.analysis?.map_notes && <p className="mt-2 text-[13px] text-stone-700">{pick(rows, "developed").analysis.map_notes}</p>}</div>
                </div>
              </div>
            );
          }
          const one = pick(rows, mode);
          return (
            <div key={p.session_id} className="border border-stone-200 rounded-md p-3 bg-white" data-testid={`compare-cell-${p.assignment_id}`}>
              <p className="text-[13px] font-medium text-stone-800 mb-2">{p.assignment_title}</p>
              {rows ? <><ThinkingMap analysis={one?.analysis} />{one?.analysis?.map_notes && <p className="mt-2 text-[13px] text-stone-700">{one.analysis.map_notes}</p>}</> : <p className="text-[12px] text-stone-400">Loading…</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ACROSS ASSIGNMENTS — restrained overview, clickable assignments, and select-up-to-4 → Compare. Maps primary in compare.
export function IstAcross({ courseId, studentId, onOpenAssignment }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [comparing, setComparing] = useState(null);
  const load = useCallback(async () => setData(await cCourseIstDevelopment(courseId)), [courseId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-[13px] text-stone-400" data-testid="ist-across-loading">Loading…</p>;
  const student = data.series.find((s) => s.student_id === studentId);
  if (!student) return <p className="text-[13px] text-stone-400">No thinking data for this student.</p>;
  const analyzedAssignments = data.assignments.filter((a) => student.points[a.order]?.analyzed);
  const trend = data.assignments.map((a) => {
    const p = student.points[a.order];
    const an = p?.analyzed;
    return {
      name: a.title.length > 16 ? a.title.slice(0, 15) + "…" : a.title,
      initial: an ? (p.entry?.ist ?? null) : null,
      developed: an ? (p.final?.ist ?? null) : null,
    };
  });
  const toggle = (a) => setSel((s) => s.find((x) => x.assignment_id === a.id) ? s.filter((x) => x.assignment_id !== a.id)
    : (s.length >= 4 ? s : [...s, { assignment_id: a.id, assignment_title: a.title, session_id: student.points[a.order].session_id }]));

  if (comparing) return <IstCompare picks={comparing} onClose={() => setComparing(null)} />;

  return (
    <div data-testid="ist-across">
      <IstOrientation />
      <p className="mt-3 text-[13px] text-stone-500 max-w-3xl">How is the organization of this student's thinking changing across pieces? Open one assignment to inspect Initial vs Developed, or select up to 4 to compare. Scores are secondary — read the maps.</p>
      {trend.some((t) => t.initial != null || t.developed != null) && (
        <div className="mt-4" data-testid="ist-across-trend-wrap">
          <h4 className="text-[12px] font-mono uppercase tracking-wider text-stone-400 mb-2">Thinking Structure over assignments</h4>
          <IstTrendChart testid="ist-across-trend" data={trend}
            lines={[{ key: "initial", name: "Initial", color: "#B08968", dashed: true }, { key: "developed", name: "Developed", color: IST }]} />
        </div>
      )}
      <div className="mt-4 bg-white border border-stone-200 rounded-md overflow-x-auto" data-testid="ist-across-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-stone-200 bg-stone-50">
            <th className="px-3 py-2 w-8"></th>
            <th className="text-left px-4 py-2 text-[12px] font-mono uppercase tracking-wider text-stone-400">Assignment</th>
            <th className="text-left px-4 py-2 text-[12px] font-mono uppercase tracking-wider text-stone-400">Initial → Developed</th>
            <th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {data.assignments.map((a) => {
              const p = student.points[a.order];
              const checked = !!sel.find((x) => x.assignment_id === a.id);
              return (
                <tr key={a.id} className="border-b border-stone-100 last:border-0" data-testid={`ist-row-${a.id}`}>
                  <td className="px-3 py-2 text-center">
                    <input type="checkbox" checked={checked} disabled={!p?.analyzed || (!checked && sel.length >= 4)}
                      onChange={() => toggle(a)} data-testid={`ist-check-${a.id}`} />
                  </td>
                  <td className="px-4 py-3 text-stone-800 font-medium">{a.title}</td>
                  <td className="px-4 py-3 text-[11px] text-stone-400" data-testid={`ist-indicator-${a.id}`}>
                    {p?.analyzed ? <span>{p.entry?.form || "—"} <span className="text-stone-300 mx-0.5">→</span> {p.final?.form || "—"}</span> : <span className="text-stone-300">not analyzed</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {p?.session_id && p?.analyzed && <button className="text-[13px] font-medium text-[#2F6F6A] hover:underline" data-testid={`ist-open-${a.id}`}
                      onClick={() => onOpenAssignment && onOpenAssignment(p.session_id, a.title)}>Inspect maps →</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button disabled={sel.length < 2} onClick={() => setComparing(sel)} data-testid="ist-compare-btn"
          className="px-4 py-2 rounded-sm text-sm text-white disabled:opacity-40" style={{ background: "#2F6F6A" }}>
          Compare selected assignments ({sel.length})
        </button>
        <span className="text-[12px] text-stone-400">Select up to 4 assignments to compare.</span>
      </div>
      <p className="mt-2 text-[11px] text-stone-400">A lower Developed coordinate is a different performance, not a decline.</p>
      <ThinkingLevelReference />
    </div>
  );
}
